/**
 * Money Club - the Worthly team's hub: a card on Home that opens a full page
 * with four section buttons, the latest post, recent posts, each section's own
 * page and a reading view for longer, formatted posts. Newsletter style: a soft theme
 * tint in Pink, Blue and Warm, white in Neutral.
 *
 * - Posts and sections come from the backend (/api/club/feed). Section names,
 *   emojis, descriptions and order are set in the dashboard. The app only owns
 *   the four section colours (per theme).
 * - Members never see reaction counts, only which emojis they tapped themselves.
 * - "Share a win" sends a win privately to the Worthly team (/api/club/wins).
 *   "Ask a question" sends a question privately (/api/club/questions).
 * - A view is counted when a post is opened in the reading view.
 * - Stored on the account under user_data.money_club:
 *     show_on_home  false hides the Home card (Settings switch)
 *     seen_post_at  newest post the user has seen, for the "New" tags
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import customToast from "../../utils/customToast";
import { keychainService } from "../../services/keychainService";
import worthlyLogo from "../../assets/money-club-worthly.png";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_06
import { useSwipeBack } from "../../utils/swipeBack"; // w2_21

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SERIF = "'DM Serif Display', serif";
const SANS = "'DM Sans', sans-serif";
const INK = '#1F2937';
const MUTED = '#6B7280';
const WIN_MAX = 280;
// Optional place for members to chat with each other (linked at the bottom of the hub).
const FACEBOOK_GROUP_URL = 'https://www.facebook.com/share/g/1FNq9qxh5w/';
const QUESTION_MAX = 500;

export const OPEN_MONEY_CLUB_EVENT = 'worthly:open-money-club';

// Opening the Money Club from outside Home (a notification tap, Settings).
// App switches to Home on the event; Home opens the page on the event, or on
// mount if it wasn't showing yet.
let pendingOpen = false;
export const requestOpenMoneyClub = () => {
  pendingOpen = true;
  window.dispatchEvent(new Event(OPEN_MONEY_CLUB_EVENT));
};
export const consumePendingMoneyClubOpen = () => {
  const was = pendingOpen;
  pendingOpen = false;
  return was;
};

// Tints are Home's shortcut tile colours for each theme.
const PALETTES = {
  pink: {
    head: '#D6388A', headSoft: '#4A4F57',
    headBg: 'radial-gradient(circle 110px at 16% 18%, rgba(255,240,248,.6), rgba(255,240,248,0)), radial-gradient(circle 110px at 70% 79%, rgba(236,205,251,.6), rgba(236,205,251,0)), linear-gradient(120deg, #fbe3f3 0%, #f1d8f7 30%, #e6d4fa 58%, #d6d9fb 82%, #dfe2fd 100%)',
    wordmark: 'linear-gradient(90deg, #D6388A 0%, #8B2FE0 100%)', headTint: '#F9DCEA', tip: '#FBEFCB',
    page: '#FBEFF5', eyebrow: '#7B5EA7', accent: '#D6388A', heart: '\u{1F49C}', rxOn: '#F2B8CF',
    tints: ['#FAE3EC', '#E9E1F5', '#E7F1DB', '#DBE7F5'],
    hero: '#F7D9E6', edge: '#F4E1EA', glow: 'rgba(194,58,114,0.07)',
    // [deeper tint, ink] for win initials and stat tiles, same order as tints
    pairs: [['#F3D0DF', '#C23A72'], ['#D5C7EB', '#7A5FB0'], ['#D3E5BE', '#5C7A4A'], ['#C4D6ED', '#3C6693']],
  },

  warm: {
    head: '#2D5A3D', headSoft: '#4A4F57',
    headBg: 'radial-gradient(circle 110px at 16% 18%, rgba(255,250,240,.7), rgba(255,250,240,0)), radial-gradient(circle 110px at 70% 79%, rgba(226,236,214,.7), rgba(226,236,214,0)), linear-gradient(120deg, #fbefe0 0%, #f7e5d6 30%, #efe6d4 58%, #e2ebd8 82%, #e8efe0 100%)',
    wordmark: 'linear-gradient(90deg, #B8642A 0%, #2A9A4E 100%)', headTint: '#E1EBDD', tip: '#FBEFCB',
    page: '#FAF7F2', eyebrow: '#2D5A3D', accent: '#2D5A3D', heart: '\u{1F49A}', rxOn: '#BFD1A6',
    tints: ['#E9EFD9', '#F7E7D9', '#F0E3D9', '#EDE3D5'],
    hero: '#EFE5D4', edge: '#ECE3D5', glow: 'rgba(45,90,61,0.07)',
    pairs: [['#DCE6C1', '#2D5A3D'], ['#F0D8C2', '#9B5A22'], ['#E5D0C2', '#7C5C3A'], ['#E2D3BC', '#7A6331']],
  },

  blue: {
    head: '#3A4DA8', headSoft: '#4A4F57',
    headBg: 'radial-gradient(circle 110px at 16% 18%, rgba(244,242,255,.7), rgba(244,242,255,0)), radial-gradient(circle 110px at 70% 79%, rgba(205,222,251,.7), rgba(205,222,251,0)), linear-gradient(120deg, #ece3fb 0%, #e2ddfa 30%, #d8defb 58%, #d2e4f6 82%, #dcefef 100%)',
    wordmark: 'linear-gradient(90deg, #3A4DA8 0%, #7B2FE8 100%)', headTint: '#DCE3F8', tip: '#FBEFCB',
    page: '#EEF2FF', eyebrow: '#4361EE', accent: '#3A4DA8', heart: '\u{1F499}', rxOn: '#B7C6F2',
    tints: ['#E1E8FA', '#E4E1F5', '#DCEFF0', '#E6E6F5'],
    hero: '#DCE3FB', edge: '#DFE5F8', glow: 'rgba(58,77,168,0.07)',
    pairs: [['#D2DEF5', '#3A4DA8'], ['#D2CDEB', '#6B4FB0'], ['#C9E4E6', '#2F6F78'], ['#D6D6ED', '#4338CA']],
  }
};
export const clubPalette = (theme) => PALETTES[theme] || PALETTES.pink;


// plan_06: Money plan - the lilac page all the way down, no gradient header.
const PLAN_CLUB = {
  plan: true,
  head: '#26252E', headSoft: '#8B879A',
  headBg: '#F4F1FB',
  wordmark: '#26252E', headTint: '#ECE6FA', tip: '#FCF3D2',
  page: '#F4F1FB', eyebrow: '#8B879A', accent: '#1F1E24', heart: '\u{1F49C}', rxOn: '#B9A6F0',
  tints: ['#FAE4ED', '#ECE6FA', '#DFF2E7', '#DFE9F9'],
  hero: '#ECE6FA', edge: '#EEEAF6', glow: 'rgba(60,40,120,0.05)',
  pairs: [['#FAE4ED', '#C04B7C'], ['#ECE6FA', '#7A5DD3'], ['#DFF2E7', '#2F8756'], ['#DFE9F9', '#3C6DB5']],
};

// moneyclub_gradient_03 + moneyclub_gradient_05: header titles fade from the theme colour to a neon tone.
const gradTitle = (P) => (P && P.plan ? { color: '#26252E' } : { display: 'inline-block', background: P.wordmark, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', WebkitTextFillColor: 'transparent', paddingBottom: 4 });

// Home card icon background: each theme's Wishlist tile, so the 💌 stands out.
const CARD_CHIP = {
  pink: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)',
  warm: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)',
  blue: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)'
};

const authHeaders = async () => {
  let token = keychainService.getCached();
  if (!token) {
    try { token = await keychainService.getItem('session_token'); } catch (e) { token = null; }
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = async (path, options = {}) => {
  const headers = { 'Content-Type': 'application/json', ...(await authHeaders()) };
  const res = await fetch(`${API_URL}/api${path}`, { ...options, headers });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const err = new Error((data && data.detail) || 'Something went wrong');
    err.status = res.status;
    throw err;
  }
  return data;
};

// Posts already counted as viewed during this app session.
const viewedThisSession = new Set();
const recordView = (postId) => {
  if (!postId || viewedThisSession.has(postId)) return;
  viewedThisSession.add(postId);
  api(`/club/posts/${encodeURIComponent(postId)}/view`, { method: 'POST' }).catch(() => {});
};

// Splits a leading emoji off a title: "💡 Tip of the week" -> ["💡", "Tip of the week"].
// No leading emoji -> [null, title].
let LEAD_EMOJI = null;
try {
  LEAD_EMOJI = new RegExp('^((?:\\p{Extended_Pictographic}|\\p{Regional_Indicator})(?:\\uFE0F|\\u200D(?:\\p{Extended_Pictographic})|\\p{Emoji_Modifier}|\\p{Regional_Indicator})*)\\s*', 'u');
} catch (e) {
  LEAD_EMOJI = null;
}
const splitTitle = (title) => {
  const t = String(title || '').trim();
  const m = LEAD_EMOJI ? t.match(LEAD_EMOJI) : null;
  if (m && m[1] && t.length > m[0].length) return [m[1], t.slice(m[0].length)];
  return [null, t];
};

const isNewer = (a, b) => !!a && (!b || String(a) > String(b));

const dateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Loads the feed and keeps it fresh when the app comes back to the front. */
// Last feed we loaded, so Home can show the card straight away (after a tab
// switch or when the app is reopened), then refresh quietly. Also saved on the
// phone so it survives the app being closed. Only the posts are saved, not the
// person's own reactions.
const FEED_STORE_KEY = 'worthly_money_club_feed_v1';
const readStoredFeed = () => {
  try {
    const raw = window.localStorage.getItem(FEED_STORE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    return data && Array.isArray(data.posts) ? data : null;
  } catch (e) {
    return null;
  }
};
const storeFeed = (data) => {
  try {
    if (!data) return;
    const posts = (data.posts || []).slice(0, 40);
    window.localStorage.setItem(FEED_STORE_KEY, JSON.stringify({ ...data, posts, my_reactions: {} }));
  } catch (e) {
    // Storage full or unavailable: the card just loads a moment later.
  }
};
let feedCache = null;
// True once a load has failed with nothing saved to show (offline, signed out):
// then the Home card hides instead of showing its placeholder.
let feedFailed = false;

export const useMoneyClubFeed = (enabled = true) => {
  const [feed, setFeedState] = useState(() => {
    if (!feedCache) feedCache = readStoredFeed();
    return feedCache;
  });
  const setFeed = useCallback((data) => { feedCache = data; feedFailed = false; setFeedState(data); }, []);
  const [, setFailedTick] = useState(0);
  const load = useCallback(async () => {
    try {
      const data = await api('/club/feed');
      setFeed(data);
      storeFeed(data);
    } catch (e) {
      // Offline or not signed in: keep whatever we had, or hide the card.
      if (!feedCache) { feedFailed = true; setFailedTick((n) => n + 1); }
    }
  }, [setFeed]);
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [enabled, load]);
  return { feed, setFeed, reload: load };
};


// ---------- Sections, emojis and text helpers ----------

// Section colour slots (from the server): 0 pink, 1 purple, 2 blue, 3 green.
// Index into each theme's tints/pairs, which are ordered pink, purple, green, blue.
const SLOT_TO_TINT = [0, 1, 3, 2];
const sectionColours = (P, slot) => {
  const i = SLOT_TO_TINT[((Number(slot) || 0) % 4 + 4) % 4];
  const [deep, ink] = P.pairs[i];
  return { tile: `linear-gradient(135deg, ${P.tints[i]} 0%, ${deep} 100%)`, ink, light: P.tints[i], deep };
};
const NO_SECTION = { id: null, name: 'Money Club', emoji: '\u{1F48C}', description: '', color: 0 };

// "Guides are", "Wins are", but "News is", "Sunday Reset is".
const isPluralName = (name) => {
  const n = String(name || '').trim().toLowerCase();
  return /[^s]s$/.test(n) && !/news$/.test(n);
};

const sectionFor = (feed, post) => (feed?.sections || []).find((s) => s.id === post?.section_id) || null;

/** Cover emoji and title to show: the post's own emoji, else a leading emoji
 *  typed into the title, else the section's emoji, else 💌. */
const postDisplay = (post, section) => {
  const [lead, rest] = splitTitle(post?.title);
  const own = String(post?.emoji || '').trim();
  const emoji = own || lead || (section && section.emoji) || '\u{1F48C}';
  return { emoji, title: own ? String(post?.title || '').trim() : rest };
};

/** Plain text of a formatted body, for previews and read time. */
const plainText = (body) => String(body || '')
  .split('\n')
  .map((l) => l.replace(/^\s*(#{1,3}\s+|[-*]\s+|\d+[.)]\s+|>\s?|!tip\s*)/i, '').replace(/^\s*---+\s*$/, ''))
  .join('\n')
  .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2');

const previewLine = (post) => {
  if (post?.summary) return post.summary;
  return plainText(post?.body).split('\n').map((l) => l.trim()).find(Boolean) || '';
};

const readMinutes = (post) => {
  const words = plainText(post?.body).split(/\s+/).filter(Boolean).length
    + (post?.wins || []).reduce((n, w) => n + String(w.text || '').split(/\s+/).length, 0);
  return Math.max(1, Math.round(words / 200));
};

// ---------- Formatting ----------
// Posts use simple markers the dashboard's buttons insert:
//   ## Heading   ### Smaller heading   - bullet   1. numbered   > quote
//   !tip text    ---  (divider)        **bold**   *italic*       [text](https://link)
// (No lookbehind in these regexes: older iOS versions can't parse it.)
// Anything else is a normal paragraph, so older plain posts look the same.

const renderInline = (text, keyBase = 'i') => {
  const out = [];
  const re = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\))|(\*([^*\n]+)\*)/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${k++}`;
    if (m[2]) out.push(<b key={key} style={{ fontWeight: 700 }}>{m[2]}</b>);
    else if (m[4]) out.push(<a key={key} href={m[5]} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>{m[4]}</a>);
    else if (m[7]) out.push(<i key={key}>{m[7]}</i>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const withBreaks = (lines, keyBase) => lines.flatMap((l, i) => (i === 0 ? renderInline(l, `${keyBase}-${i}`) : [<br key={`${keyBase}-br${i}`} />, ...renderInline(l, `${keyBase}-${i}`)]));

/** Turns a post body into blocks. Pure, so it's easy to test. */
export const parseBody = (body) => {
  const lines = String(body || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  const flush = () => { if (para.length) { blocks.push({ type: 'p', lines: para }); para = []; } };
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (!line) { flush(); continue; }
    let m;
    if ((m = line.match(/^(#{2,3})\s+(.*)$/)) || (m = line.match(/^(#)\s+(.*)$/))) {
      flush(); blocks.push({ type: m[1].length === 3 ? 'h3' : 'h2', text: m[2] }); continue;
    }
    if (/^---+$/.test(line) || /^\*\*\*+$/.test(line)) { flush(); blocks.push({ type: 'hr' }); continue; }
    if ((m = line.match(/^!tip\s*:?\s*(.*)$/i))) {
      flush();
      const tip = [m[1]];
      while (i + 1 < lines.length && lines[i + 1].trim() && !/^(#|[-*]\s|\d+[.)]\s|>|!tip|---)/i.test(lines[i + 1].trim())) tip.push(lines[++i].trim());
      blocks.push({ type: 'tip', lines: tip.filter((t, j) => t || j > 0) });
      continue;
    }
    if (line.startsWith('>')) {
      flush();
      const q = [line.replace(/^>\s?/, '')];
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('>')) q.push(lines[++i].trim().replace(/^>\s?/, ''));
      blocks.push({ type: 'quote', lines: q });
      continue;
    }
    if (/^[-*•]\s+/.test(line)) {
      flush();
      const items = [line.replace(/^[-*•]\s+/, '')];
      while (i + 1 < lines.length && /^[-*•]\s+/.test(lines[i + 1].trim())) items.push(lines[++i].trim().replace(/^[-*•]\s+/, ''));
      blocks.push({ type: 'ul', items });
      continue;
    }
    if (/^\d+[.)]\s+/.test(line)) {
      flush();
      const items = [line.replace(/^\d+[.)]\s+/, '')];
      while (i + 1 < lines.length && /^\d+[.)]\s+/.test(lines[i + 1].trim())) items.push(lines[++i].trim().replace(/^\d+[.)]\s+/, ''));
      blocks.push({ type: 'ol', items });
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
};

const FormattedBody = ({ body, P }) => {
  const blocks = parseBody(body);
  if (!blocks.length) return null;
  const pStyle = { fontSize: 15.5, lineHeight: 1.65, margin: '0 0 14px', color: INK };
  return (
    <div data-testid="money-club-body">
      {blocks.map((b, i) => {
        const k = `b${i}`;
        switch (b.type) {
          case 'h2': return <h2 key={k} style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 23, lineHeight: 1.2, margin: '24px 0 8px', color: '#1A1A1F' }}>{renderInline(b.text, k)}</h2>;
          case 'h3': return <h3 key={k} style={{ fontSize: 16.5, fontWeight: 700, lineHeight: 1.3, margin: '20px 0 6px', color: '#1A1A1F' }}>{renderInline(b.text, k)}</h3>;
          case 'hr': return <div key={k} aria-hidden="true" style={{ textAlign: 'center', color: '#D1D5DB', letterSpacing: 10, margin: '20px 0' }}>• • •</div>;
          case 'tip': return (
            <div key={k} style={{ background: (P && P.tip) || '#FBEFCB', borderRadius: 18, padding: '16px', margin: '18px 0', fontSize: 15, lineHeight: 1.55, color: INK }}>
              <span className="block" style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>💡 Worthly tip</span>
              {withBreaks(b.lines, k)}
            </div>
          );
          case 'quote': return <blockquote key={k} style={{ borderLeft: `3px solid ${(P && P.head) || INK}`, margin: '18px 0', padding: '4px 0 4px 14px', fontFamily: SERIF, fontSize: 20, lineHeight: 1.35, color: '#1A1A1F' }}>{withBreaks(b.lines, k)}</blockquote>;
          case 'ul':
          case 'ol': {
            const Tag = b.type;
            return (
              <Tag key={k} style={{ margin: '0 0 14px', paddingLeft: 22, fontSize: 15.5, lineHeight: 1.6, color: INK, listStyleType: b.type === 'ul' ? 'disc' : 'decimal' }}>
                {b.items.map((it, j) => <li key={j} style={{ marginBottom: 6 }}>{renderInline(it, `${k}-${j}`)}</li>)}
              </Tag>
            );
          }
          default: return <p key={k} style={pStyle}>{withBreaks(b.lines, k)}</p>;
        }
      })}
    </div>
  );
};

// ---------- Home card ----------

/** The card on Home. Before the first post it shows a "coming soon" welcome. */
export const MoneyClubCard = ({ feed, userData, theme = 'pink', onOpen }) => {
  const P = clubPalette(theme);
  const purple = usePurple();
  const cardCls = 'w-full bg-white rounded-2xl shadow-sm flex items-center gap-3 text-left';
  const cardStyle = { padding: '13px 14px' };
  const chipBg = purple ? '#F3E8FB' : (CARD_CHIP[theme] || CARD_CHIP.pink);
  const settings = userData?.money_club || {};
  if (settings.show_on_home === false) return null;
  if (!feed) {
    if (feedFailed) return null;
    // First ever open: show the card straight away with a soft placeholder
    // while the posts load, so it doesn't pop in a moment later.
    return (
      <div className="mb-6" data-testid="money-club-home">
        <div className="mb-2 px-1">
          <p className="text-[13px] font-bold tracking-[0.08em] uppercase m-0" style={{ color: P.eyebrow }}><span className="wy-star">✦ </span>Money Club</p>
        </div>
        <button type="button" onClick={onOpen} className={cardCls} style={cardStyle} data-testid="money-club-card" aria-busy="true">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: chipBg, fontSize: 21 }}>💌</span>
          <span className="flex-1 min-w-0">
            <span className="block truncate" style={{ fontSize: 14.5, fontWeight: 600, color: INK }}>Money Club</span>
            <span className="block" style={{ height: 10, width: '62%', borderRadius: 6, background: '#F1EEF0', marginTop: 7 }} />
          </span>
          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        </button>
      </div>
    );
  }
  const posts = feed.posts || [];
  const latest = posts.find((p) => p.id === feed.latest_post_id) || posts[0] || null;
  const isNew = !!latest && isNewer(feed.latest_post_at, settings.seen_post_at);
  const title = latest ? postDisplay(latest, sectionFor(feed, latest)).title : 'Welcome to the Money Club';
  const preview = latest ? previewLine(latest) : 'Coming soon: resets, guides and wins';
  return (
    <div className="mb-6" data-testid="money-club-home">
      <div className="mb-2 px-1">
        <p className="text-[13px] font-bold tracking-[0.08em] uppercase m-0" style={{ color: P.eyebrow }}><span className="wy-star">✦ </span>Money Club</p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className={cardCls}
        style={cardStyle}
        data-testid="money-club-card"
      >
        <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: chipBg, fontSize: 21 }}>💌</span>
        <span className="flex-1 min-w-0">
          <span className="block truncate" style={{ fontSize: 14.5, fontWeight: 600, color: INK }}>{title}</span>
          {preview && <span className="block truncate" style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>{preview}</span>}
        </span>
        {isNew ? (
          <span className="flex-shrink-0 rounded-full" style={{ background: P.accent, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px' }}>New</span>
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        )}
      </button>
    </div>
  );
};

// ---------- Page pieces ----------

const NewTag = ({ P }) => (
  <span className="rounded-full flex-shrink-0" style={{ background: P.accent, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>New</span>
);

// Newsletter masthead at the top of the hub: today's date, the title and a
// one-line description, with a line underneath.
const Masthead = ({ P }) => {
  if (P.plan) {
    return (
      <div style={{ padding: '4px 2px 0' }} data-testid="money-club-masthead">
        <h1 style={{ margin: 0 }}>{'\u{1F48C}'} money club</h1>{/* w2_11 */}
        <span className="block" style={{ fontSize: 14.5, color: '#8B879A', marginTop: 6, lineHeight: 1.4 }}>Money reads, insights and wins from the Worthly team!</span>
      </div>
    );
  }
  // moneyclub_bright_01: sits in the bright full-width header, so text is white.
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div style={{ padding: '4px 2px 0' }} data-testid="money-club-masthead">
      <span className="block" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: P.eyebrow }}>{today}</span>
      <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 34, lineHeight: 1.05, margin: '4px 0 0', ...gradTitle(P) }}>Money Club</h1>
      <span className="block" style={{ fontSize: 14, color: P.headSoft, marginTop: 6, lineHeight: 1.45 }}>Money reads, insights and wins from the Worthly team!</span>
    </div>
  );
};

// Section menu under the masthead: an even row of equal-size buttons, emoji on
// top and the name underneath, so a longer name doesn't make one wider than the
// rest. Soft section colours in Pink, Blue and Warm; no background in Neutral.
// A small dot marks a section with a post the member hasn't seen yet.
const SectionMenu = ({ sections, P, newIds, counts, onOpen }) => (
  <div
    className="grid"
    style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 18 }}
    data-testid="money-club-section-menu"
  >
    {sections.map((s) => {
      const n = counts[s.id] || 0;
      return (
        <button
          key={s.id}
          type="button"
          onClick={() => onOpen(s.id)}
          className="relative flex items-center text-left"
          style={P.plan
            ? { border: 0, borderRadius: 22, background: sectionColours(P, s.color).light, padding: 15, gap: 12, minWidth: 0, flexDirection: 'column', alignItems: 'flex-start' }
            : { border: '1px solid rgba(255,255,255,0.75)', borderRadius: 16, background: 'rgba(255,255,255,0.6)', WebkitBackdropFilter: 'blur(10px)', backdropFilter: 'blur(10px)', padding: '11px 12px', gap: 10, fontFamily: SANS, minWidth: 0 }}
          data-testid={`money-club-section-${s.id}`}
        >
          <span className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(255,255,255,0.75)', fontSize: 18, lineHeight: 1 }}>{s.emoji || '\u{1F48C}'}</span>
          <span className="min-w-0">
            <span className="block" style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.2, color: INK, overflowWrap: 'anywhere' }}>{s.name}</span>
            <span className="block" style={{ fontSize: 11.5, color: P.headSoft, marginTop: 2 }}>{n} {n === 1 ? 'post' : 'posts'}</span>
          </span>
          {newIds.has(s.id) && <span aria-label="New" style={{ position: 'absolute', top: 9, right: 9, width: 8, height: 8, borderRadius: 999, background: P.accent }} />}
        </button>
      );
    })}
  </div>
);

/** Big card: cover colour + emoji, then section, title, summary, byline. */

/** Lead story: outlined card with the post's emoji in a corner tile, section label,
 *  big serif title, summary, byline and a "Read the post" button. */
const FeatureCard = ({ post, section, P, isNew, onOpen }) => {
  const { emoji, title } = postDisplay(post, section);
  const c = sectionColours(P, (section || NO_SECTION).color);
  const dek = previewLine(post);
  return (
    <button type="button" onClick={() => onOpen(post.id)} className="w-full text-left" style={{ border: 0, borderRadius: 24, padding: '18px 18px 20px', background: '#fff', boxShadow: '0 2px 14px rgba(35,38,43,0.06), 0 1px 3px rgba(35,38,43,0.04)', fontFamily: SANS, display: 'block' }} data-open-post={post.id} data-testid="money-club-lead">
      <span className="flex items-center" style={{ gap: 10 }}>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 14, background: c.light, fontSize: 19 }}>{emoji}</span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center" style={{ gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>{section ? section.name : 'Money Club'}</span>
            {isNew && <NewTag P={P} />}
            {!isNew && post.pinned && <span style={{ fontSize: 12 }}>📌</span>}
          </span>
          <span className="block" style={{ fontSize: 12, color: '#8A8F96', marginTop: 1 }}>{post.pinned ? 'Pinned' : dateLabel(post.created_at)} · {readMinutes(post)} min read</span>
        </span>
        {/* moneyclub_bright_02: the whole card opens the post; the arrow hints at it. */}
        <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: '#C9C5CB' }} />
      </span>
      <span className="block" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 25, lineHeight: 1.12, color: '#1A1A1F', margin: '14px 0 0', overflowWrap: 'anywhere' }}>{title}</span>
      {dek && <span className="block" style={{ fontSize: 14.5, color: '#4A4F57', lineHeight: 1.5, marginTop: 6, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{dek}</span>}
      {P.plan && (
        <span className="block" style={{ marginTop: 16, background: '#1F1E24', color: '#fff', borderRadius: 999, padding: 14, textAlign: 'center', fontSize: 15, fontWeight: 600 }}>Read</span>
      )}
    </button>
  );
};

/** List row: small emoji tile, section name, serif title, date. Thin line between rows. */
const PostRow = ({ post, section, P, isNew, onOpen, showSection = true }) => {
  const { emoji, title } = postDisplay(post, section);
  const c = sectionColours(P, (section || NO_SECTION).color);
  const bits = [post.pinned ? 'Pinned' : dateLabel(post.created_at)];
  if (readMinutes(post) >= 3) bits.push(`${readMinutes(post)} min read`);
  if (showSection) bits.unshift(section ? section.name : 'Money Club');
  return (
    <button type="button" onClick={() => onOpen(post.id)} className="w-full flex items-center text-left" style={{ gap: 12, border: 0, borderBottom: '1px solid #F1EEF0', background: 'none', padding: '13px 0', fontFamily: SANS }} data-open-post={post.id}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: c.light, fontSize: 19 }}>{emoji}</span>
      <span className="flex-1 min-w-0">
        <span className="flex items-start" style={{ gap: 6 }}>
          <span style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.3, color: '#1A1A1F', overflowWrap: 'anywhere' }}>{title}</span>
          {isNew && <span style={{ marginTop: 1 }}><NewTag P={P} /></span>}
        </span>
        <span className="block" style={{ fontSize: 12, color: '#8A8F96', marginTop: 2 }}>{bits.join(' · ')}</span>
      </span>
      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C9C5CB' }} />
    </button>
  );
};

/** moneyclub_bright_01: sideways swipe row of white cards under Latest. */
const MoreRow = ({ posts, feed, P, isNewPost, onOpen }) => (
  <div className="flex overflow-x-auto" style={{ gap: 10, margin: '0 -16px', padding: '2px 16px 10px', scrollSnapType: 'x mandatory', scrollPaddingLeft: 16, scrollPaddingRight: 16, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }} data-testid="money-club-more">
    {posts.map((p) => {
      const section = sectionFor(feed, p);
      const { emoji, title } = postDisplay(p, section);
      const c = sectionColours(P, (section || NO_SECTION).color);
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => onOpen(p.id)}
          className="flex flex-col text-left flex-shrink-0"
          style={{ width: 168, minHeight: 168, gap: 8, padding: 14, border: 0, borderRadius: 22, background: '#fff', boxShadow: '0 2px 14px rgba(35,38,43,0.06), 0 1px 3px rgba(35,38,43,0.04)', fontFamily: SANS, scrollSnapAlign: 'start' }}
          data-open-post={p.id}
        >
          <span className="flex items-center justify-between" style={{ width: '100%' }}>
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: c.light, fontSize: 18 }}>{emoji}</span>
            {isNewPost(p) && <NewTag P={P} />}
          </span>
          <span style={{ flex: 1, fontSize: 14.5, fontWeight: 600, lineHeight: 1.28, color: '#1A1A1F', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</span>
          <span style={{ fontSize: 11.5, color: '#8A8F96' }}>{section ? section.name : 'Money Club'} · {p.pinned ? 'Pinned' : dateLabel(p.created_at)}</span>
        </button>
      );
    })}
  </div>
);

const WinBanner = ({ P, text, onShare }) => (
  <div className="flex items-center" style={{ gap: 12, marginTop: 16, padding: 16, borderRadius: 18, background: P.soft || '#F7F5F6' }}>
    <span className="flex-1 min-w-0">
      <span className="block" style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>Got a win this week? 🎉</span>
      <span className="block" style={{ fontSize: 12.5, color: MUTED, marginTop: 2, lineHeight: 1.4 }}>{text}</span>
    </span>
    <button type="button" onClick={onShare} className="rounded-full flex-shrink-0" style={{ border: 0, background: P.accent, color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 13px', whiteSpace: 'nowrap', fontFamily: SANS }} data-testid="money-club-share-btn">
      Share a win
    </button>
  </div>
);

const AskBanner = ({ P, onAsk }) => (
  <div className="flex items-center" style={{ gap: 12, marginTop: 16, padding: 16, borderRadius: 18, background: P.soft || '#F7F5F6' }}>
    <span className="flex-1 min-w-0">
      <span className="block" style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>Got a money question? 💬</span>
      <span className="block" style={{ fontSize: 12.5, color: MUTED, marginTop: 2, lineHeight: 1.4 }}>Ask us and we might answer it in a guide!</span>
    </span>
    <button type="button" onClick={onAsk} className="rounded-full flex-shrink-0" style={{ border: 0, background: P.accent, color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 13px', whiteSpace: 'nowrap', fontFamily: SANS }} data-testid="money-club-ask-btn">
      Ask us
    </button>
  </div>
);

/** Hub card: share a win or ask a question. */
const ShareCard = ({ P, early, onShare, onAsk }) => {
  // White tiles like the section buttons; colour only in the emoji circle.
  const tile = (c, emoji, title, sub, onClick, testid) => (
    <button type="button" onClick={onClick} className="text-left" style={{ border: 0, borderRadius: 22, background: P.plan ? c.light : '#fff', boxShadow: P.plan ? 'none' : '0 2px 14px rgba(35,38,43,0.06), 0 1px 3px rgba(35,38,43,0.04)', padding: '16px 14px', fontFamily: SANS }} data-testid={testid}>
      <span className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 999, background: P.plan ? 'rgba(255,255,255,0.75)' : c.light, fontSize: 20 }}>{emoji}</span>
      <span className="block" style={{ fontSize: 14.5, fontWeight: 700, color: INK, marginTop: 10 }}>{title}</span>
      <span className="block" style={{ fontSize: 12, color: '#4A4F57', marginTop: 2, lineHeight: 1.35 }}>{sub}</span>
    </button>
  );
  return (
    <div data-testid="money-club-share-card">
      <Label>Share with us</Label>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {tile(sectionColours(P, 3), '🎉', 'Share a win', 'Big or small!', onShare, 'money-club-share-btn')}
        {tile(sectionColours(P, 2), '💬', 'Ask a question', early ? 'Yours could be in our first posts!' : 'It could become a post', onAsk, 'money-club-ask-btn')}
      </div>
    </div>
  );
};

// Section headings on the Money Club pages ("LATEST"), in the theme's
// heading colour like the rest of the app.
let labelColour = '#7B5EA7';
const Label = ({ children }) => (
  <p className="flex items-center wy-club-label" style={{ gap: 10, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: labelColour, margin: '28px 4px 10px' }}>
    {children}
  </p>
);

const Reactions = ({ post, P, mine, onReact, showHint }) => (
  <div style={{ background: P.headTint, borderRadius: 22, padding: 18, marginTop: 22, textAlign: 'center' }}>
    <span className="block" style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{mine.length ? 'Thanks for reacting! \u{1F49C}' : 'Enjoyed this read?'}</span>
    <span className="block" style={{ fontSize: 12.5, color: MUTED, marginTop: 3, lineHeight: 1.4 }}>
      {mine.length ? 'It helps us know what to share more of.' : "React so we know what you'd like to see more of!"}
    </span>
    <div className="flex flex-wrap justify-center" style={{ gap: 6, marginTop: 12 }}>
      {(post.reactions || []).map((emojiR) => {
        const on = mine.includes(emojiR);
        return (
          <button
            key={emojiR}
            type="button"
            onClick={() => onReact(post.id, emojiR)}
            aria-pressed={on}
            className="rounded-full"
            style={{ fontSize: 17, padding: '6px 14px', border: `1px solid ${on ? P.rxOn : '#EDEDED'}`, background: on ? P.tints[0] : '#fff', transition: 'transform .15s ease', transform: on ? 'scale(1.05)' : 'none' }}
          >
            {emojiR}
          </button>
        );
      })}
    </div>
  </div>
);

// How much of the bottom of the screen the keyboard is covering. Uses the
// keyboard plugin's height when it gives one, minus anything the WebView
// already shrank by, and falls back to the visual viewport.
const useKeyboardCover = () => {
  const [cover, setCover] = useState(0);
  useEffect(() => {
    const baseH = window.innerHeight;
    let kb = 0;
    const compute = () => {
      const fromEvent = kb ? Math.max(0, kb - (baseH - window.innerHeight)) : 0;
      const vv = window.visualViewport;
      const fromVV = vv ? Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0)) : 0;
      setCover(Math.round(Math.max(fromEvent, fromVV)));
    };
    const onShow = (e) => { kb = (e && e.keyboardHeight) || kb || 0; compute(); };
    const onHide = () => { kb = 0; setCover(0); };
    window.addEventListener('keyboardWillShow', onShow);
    window.addEventListener('keyboardDidShow', onShow);
    window.addEventListener('keyboardWillHide', onHide);
    window.addEventListener('keyboardDidHide', onHide);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', compute);
      vv.addEventListener('scroll', compute);
    }
    return () => {
      window.removeEventListener('keyboardWillShow', onShow);
      window.removeEventListener('keyboardDidShow', onShow);
      window.removeEventListener('keyboardWillHide', onHide);
      window.removeEventListener('keyboardDidHide', onHide);
      if (vv) {
        vv.removeEventListener('resize', compute);
        vv.removeEventListener('scroll', compute);
      }
    };
  }, []);
  return cover;
};

const SHEET_COPY = {
  win: {
    path: '/club/wins',
    max: WIN_MAX,
    title: 'Share a win 🎉',
    intro: "Big or small, it all counts. We'd love to hear about your win, and share it with the Worthly community!",
    placeholder: 'Finally stuck to my grocery budget for a whole month!',
    send: 'Send my win',
    thanks: ['Thanks for sharing! 🎉', "We can't wait to read it"],
    fail: "Couldn't send your win",
    foot: "Wins are read by the Worthly team before they're shared.",
    named: true,
  },
  question: {
    path: '/club/questions',
    max: QUESTION_MAX,
    title: 'Ask a question 💬',
    intro: 'Ask us anything about money or Worthly, and your question could be answered in the Money Club!',
    placeholder: 'How much should I keep in my emergency fund?',
    send: 'Send my question',
    thanks: ['Thanks for your question! 💬', "We'll try to answer it in the Money Club"],
    fail: "Couldn't send your question",
    foot: 'We answer in general terms, not as personal financial advice.',
    named: false,
  },
};

const ShareWinSheet = ({ P, firstName, onClose, kind = 'win' }) => {
  const copy = SHEET_COPY[kind] || SHEET_COPY.win;
  const cover = useKeyboardCover();
  const [text, setText] = useState('');
  // Wins show a first name by default; questions start anonymous.
  const [showName, setShowName] = useState(copy.named);
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api(copy.path, { method: 'POST', body: JSON.stringify({ text: text.trim(), show_name: showName }) });
      customToast.success(copy.thanks[0], copy.thanks[1]);
      onClose();
    } catch (e) {
      customToast.error(copy.fail, e.message || 'Please try again');
      setSending(false);
    }
  };
  const segBtn = (on) => ({ flex: 1, textAlign: 'center', padding: 8, borderRadius: 999, fontSize: 13.5, fontWeight: 600, border: 0, background: on ? '#fff' : 'transparent', color: on ? INK : MUTED, boxShadow: on ? '0 1px 2px rgba(0,0,0,.08)' : 'none', fontFamily: SANS });
  return (
    <div className="fixed inset-0" style={{ zIndex: 260 }}>
      <div className="absolute inset-0" style={{ background: 'rgba(20,16,30,.38)' }} onClick={onClose} />
      {/* Sits at the top of the screen so it never moves when the keyboard opens.
          It only shrinks (and scrolls) if the keyboard would cover the bottom. */}
      <div
        className="absolute left-0 right-0 bg-white"
        style={{
          top: 'calc(env(safe-area-inset-top) + 12px)',
          margin: '0 10px',
          borderRadius: 24,
          padding: '16px 20px 20px',
          maxHeight: `calc(100% - env(safe-area-inset-top) - 24px - ${cover}px)`,
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(20,16,30,.18)',
          fontFamily: SANS,
        }}
        data-testid="money-club-share-sheet"
      >
        <div className="flex items-start" style={{ gap: 10 }}>
          <h3 className="flex-1" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 25, margin: '0 0 4px', color: INK }}>{copy.title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="flex items-center justify-center flex-shrink-0 rounded-full" style={{ width: 32, height: 32, border: 0, background: '#F3F4F6', color: MUTED }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <p style={{ fontSize: 13.5, color: '#4B5563', margin: '0 0 14px', lineHeight: 1.45 }}>{copy.intro}</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, copy.max))}
          placeholder={copy.placeholder}
          rows={4}
          className="w-full"
          style={{ border: '1.5px solid #E5E7EB', borderRadius: 14, padding: 12, fontSize: 16, lineHeight: 1.45, fontFamily: SANS, resize: 'none', outline: 'none', color: INK }}
          data-testid="money-club-win-input"
        />
        <p style={{ fontSize: 11.5, color: '#9CA3AF', textAlign: 'right', margin: '4px 0 0' }}>{text.length}/{copy.max}</p>
        <p style={{ fontSize: 12, fontWeight: 700, color: MUTED, margin: '8px 0 7px' }}>Show my name as</p>
        <div className="flex" style={{ background: '#F3F4F6', borderRadius: 999, padding: 4 }}>
          <button type="button" style={segBtn(showName)} onClick={() => setShowName(true)}>{firstName || 'My first name'}</button>
          <button type="button" style={segBtn(!showName)} onClick={() => setShowName(false)}>Anonymous</button>
        </div>
        <button
          type="button"
          onClick={send}
          disabled={!text.trim() || sending}
          className="w-full rounded-full"
          style={{ border: 0, background: P.accent, color: '#fff', fontSize: 15.5, fontWeight: 600, padding: 14, marginTop: 16, opacity: !text.trim() || sending ? 0.5 : 1, fontFamily: SANS }}
          data-testid="money-club-win-send"
        >
          {sending ? 'Sending…' : copy.send}
        </button>
        <p style={{ fontSize: 11.5, color: '#9CA3AF', textAlign: 'center', margin: '10px 0 0' }}>{copy.foot}</p>
      </div>
    </div>
  );
};

/** moneyclub_bright_01: an opened post's heading, shown in the bright header. */
const PostHeader = ({ post, section, P }) => {
  const { emoji, title } = postDisplay(post, section);
  return (
    <div data-testid="money-club-reader-head" style={P.plan ? { background: '#fff', borderRadius: 28, padding: 20, marginTop: 4 } : undefined}>
      <div className="flex items-center" style={{ gap: 10, marginTop: 10 }}>
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: P.plan ? sectionColours(P, (section || NO_SECTION).color).light : 'rgba(255,255,255,0.75)', fontSize: 20 }}>{emoji}</span>
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: P.eyebrow }}>{section ? section.name : 'Money Club'}</span>
      </div>
      <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 31, lineHeight: 1.12, margin: '14px 0 0', overflowWrap: 'anywhere', ...gradTitle(P) }}>{title}</h1>
      {post.summary && <p style={{ fontSize: 16, lineHeight: 1.5, color: P.headSoft, margin: '10px 0 0' }}>{post.summary}</p>}
      <div className="flex items-center" style={{ gap: 8, fontSize: 12.5, color: P.headSoft, marginTop: 16 }}>
        <img src={worthlyLogo} alt="" width={26} height={26} style={{ width: 26, height: 26, borderRadius: 999, background: '#fff' }} />
        <span><b style={{ color: INK, fontWeight: 600 }}>Worthly team</b> · {dateLabel(post.created_at)} · {readMinutes(post)} min read</span>
      </div>
    </div>
  );
};

/** Reading view for one post. */
const PostReader = ({ post, section, P, mine, onReact, showHint, next, nextSection, onOpen }) => {
  useEffect(() => { recordView(post.id); }, [post.id]);
  return (
    <div data-testid="money-club-reader" style={{ paddingTop: P.plan ? 12 : 22 }}>
      <div style={P.plan ? { background: '#fff', borderRadius: 28, padding: '22px 20px 10px' } : undefined}>
      <FormattedBody body={post.body} P={P} />

      {(post.wins || []).map((w, i) => {
        const name = (w.name || '').trim() || 'Anonymous';
        const [bg, ink] = P.pairs[i % P.pairs.length];
        return (
          <div key={i} className="flex items-start" style={{ gap: 10, padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid #F1ECEE', fontSize: 15, lineHeight: 1.5, color: INK }}>
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 30, height: 30, marginTop: 1, borderRadius: 999, background: bg, color: ink, fontSize: 13, fontWeight: 700 }}>{name.charAt(0).toUpperCase()}</span>
            <span className="flex-1 min-w-0" style={{ overflowWrap: 'anywhere' }}><b>{name}</b> {w.text}</span>
          </div>
        );
      })}

      {(post.stats || []).length > 0 && (
        <div className="grid grid-cols-2" style={{ gap: 8, margin: '10px 0 4px' }}>
          {post.stats.map((s, i) => {
            const [deep, ink] = P.pairs[i % P.pairs.length];
            return (
              <div key={i} style={{ borderRadius: 14, padding: 12, background: `linear-gradient(135deg, ${P.tints[i % P.tints.length]} 0%, ${deep} 100%)` }}>
                <span className="block" style={{ fontFamily: SERIF, fontSize: 25, lineHeight: 1.15, color: ink }}>{s.value}</span>
                <span className="block" style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      </div>

      {(post.reactions || []).length > 0 && <Reactions post={post} P={P} mine={mine} onReact={onReact} showHint={showHint} />}

      {next && (
        <>
          <Label>Up next</Label>
          <PostRow post={next} section={nextSection} P={P} isNew={false} onOpen={onOpen} />
        </>
      )}
    </div>
  );
};

/** Full-screen Money Club hub. Views: hub, a section, or one post. */
export const MoneyClubPage = ({ feed, setFeed, reload, userData, saveUserData, theme = 'pink', firstName, onClose }) => {
  // null, 'win' or 'question'
  const [shareOpen, setShareOpen] = useState(null);
  // Stack of views: { type: 'hub' } | { type: 'section', id } | { type: 'all' } | { type: 'post', id }
  const [stack, setStack] = useState([{ type: 'hub' }]);
  const view = stack[stack.length - 1];
  // moneyclub_tint_03: soft theme tint behind the white tiles and cards;
  // an opened post stays white for reading.
  const tinted = view.type !== 'post';
  // plan_06: Money plan palette - lilac page, no gradient header.
  const planMC = usePlan();
  const P = { ...(planMC ? PLAN_CLUB : clubPalette(theme)), tinted, soft: planMC ? '#FFFFFF' : '#F7F5F6' };
  labelColour = P.eyebrow;
  // Lists of post rows sit on a white card when the page is tinted.
  const listCard = (rows) => (
    <div style={{ background: '#fff', borderRadius: 24, padding: '2px 16px', boxShadow: '0 2px 14px rgba(35,38,43,0.06), 0 1px 3px rgba(35,38,43,0.04)', overflow: 'hidden' }}>
      <div style={{ marginBottom: -1 }}>{rows}</div>
    </div>
  );
  const push = (v) => setStack((s) => [...s, v]);
  const back = () => { if (stack.length > 1) setStack((s) => s.slice(0, -1)); else onClose(); };
  // Remember what was unseen when the page opened, so "New" tags stay put
  // while they read even though we mark everything seen straight away.
  const seenAtOpen = useRef((userData?.money_club || {}).seen_post_at || null);
  const scrollRef = useRef(null);
  useSwipeBack(() => scrollRef.current, () => back(), planMC); // w2_21: swipe back like the plan pages

  useEffect(() => {
    reload();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [reload]);

  // Each new view starts at the top.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [stack.length, view.type, view.id]);

  const latestAt = feed?.latest_post_at;
  useEffect(() => {
    const mc = userData?.money_club || {};
    if (latestAt && isNewer(latestAt, mc.seen_post_at)) {
      saveUserData({ money_club: { ...mc, seen_post_at: latestAt } });
    }
  }, [latestAt, userData, saveUserData]);

  const mineFor = (id) => (feed?.my_reactions || {})[id] || [];
  const hasReacted = Object.values(feed?.my_reactions || {}).some((list) => list && list.length);

  const react = async (postId, emoji) => {
    const before = feed;
    const current = mineFor(postId);
    const next = current.includes(emoji) ? current.filter((e) => e !== emoji) : [...current, emoji];
    setFeed({ ...feed, my_reactions: { ...(feed.my_reactions || {}), [postId]: next } });
    try {
      await api('/club/react', { method: 'POST', body: JSON.stringify({ post_id: postId, emoji }) });
    } catch (e) {
      setFeed(before);
      customToast.error("Couldn't save that", 'Please try again');
    }
  };

  const sections = feed?.sections || [];
  const posts = feed?.posts || [];
  const byDate = [...posts].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  const isNewPost = (p) => !p.pinned && isNewer(p.created_at, seenAtOpen.current);
  const newSectionIds = new Set(byDate.filter(isNewPost).map((p) => p.section_id).filter(Boolean));
  const openPost = (id) => push({ type: 'post', id });
  const openSection = (id) => push({ type: 'section', id });
  // Groups posts by month: "October", or "October 2025" for other years.
  const monthGroups = (list) => {
    const now = new Date();
    const groups = [];
    list.forEach((p) => {
      const d = new Date(p.created_at);
      const key = Number.isNaN(d.getTime()) ? 'Earlier' : d.toLocaleDateString('en-US', d.getFullYear() === now.getFullYear() ? { month: 'long' } : { month: 'long', year: 'numeric' });
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.posts.push(p); else groups.push({ key, posts: [p] });
    });
    return groups;
  };
  // Facebook group link at the bottom of the page. Saved on the account in the
  // same place as the old pop-up (user_data.money_club_invite), so the dashboard
  // can tell where people joined from: joined_from "money_club_page".
  const openFacebook = () => {
    const s = userData?.money_club_invite || {};
    const now = new Date().toISOString();
    const extra = {
      page_link_clicks: (s.page_link_clicks || 0) + 1,
      page_link_first_clicked_at: s.page_link_first_clicked_at || now,
      page_link_last_clicked_at: now,
    };
    if (s.status !== 'joined') Object.assign(extra, { status: 'joined', joined_at: now, joined_from: 'money_club_page' });
    try { saveUserData({ money_club_invite: { ...s, ...extra } }); } catch (e) { /* never block the link */ }
    window.open(FACEBOOK_GROUP_URL, '_blank');
  };
  const backButton = (label, light) => P.plan ? (
    <button type="button" onClick={back} aria-label="Back" className="flex items-center justify-center" style={{ width: 42, height: 42, borderRadius: 999, background: '#fff', border: 0, marginBottom: 16 }}>
      <ChevronLeft className="w-5 h-5" style={{ color: INK }} />
    </button>
  ) : (
    <button type="button" onClick={back} aria-label="Back" className="flex items-center" style={{ height: 40, marginLeft: -6, color: light ? '#fff' : INK, background: 'none', border: 0, fontSize: 14.5, fontWeight: 600, fontFamily: SANS, gap: 2 }}>
      <ChevronLeft className="w-6 h-6" />
      {label}
    </button>
  );

  const winText = posts.length ? "Share it and it might be featured!" : 'Share it now and it could be one of our first features!';

  let content = null;
  let header = null;
  let backLabel = null;

  if (!feed) {
    content = <p style={{ fontSize: 14, color: MUTED, textAlign: 'center', padding: '60px 0' }}>Loading…</p>;
  } else if (view.type === 'post') {
    const post = posts.find((p) => p.id === view.id);
    const prevView = stack[stack.length - 2];
    const prevSection = prevView && prevView.type === 'section' ? sections.find((s) => s.id === prevView.id) : null;
    backLabel = prevSection ? prevSection.name : (prevView && prevView.type === 'all' ? 'All posts' : 'Money Club');
    if (!post) {
      content = <p style={{ fontSize: 14, color: MUTED, textAlign: 'center', padding: '60px 0' }}>This post isn't available any more.</p>;
    } else {
      const section = sectionFor(feed, post);
      header = <PostHeader post={post} section={section} P={P} />;
      // Up next: the next older post in the same section (or overall if it has none).
      const pool = post.section_id ? byDate.filter((p) => p.section_id === post.section_id) : byDate;
      const idx = pool.findIndex((p) => p.id === post.id);
      const next = pool[idx + 1] || null;
      content = (
        <PostReader
          key={post.id}
          post={post}
          section={section}
          P={P}
          mine={mineFor(post.id)}
          onReact={react}
          showHint={!hasReacted}
          next={next}
          nextSection={next ? sectionFor(feed, next) : null}
          onOpen={(id) => setStack((s) => [...s.slice(0, -1), { type: 'post', id }])}
        />
      );
    }
  } else if (view.type === 'section') {
    const section = sections.find((s) => s.id === view.id) || NO_SECTION;
    const list = byDate.filter((p) => p.section_id === view.id);
    backLabel = 'Money Club';
    header = (
      <div data-testid="money-club-section-head">
        <div className="flex items-center" style={{ gap: 14, marginTop: 10 }}>
          <span className="flex items-center justify-center flex-shrink-0" style={{ width: 52, height: 52, borderRadius: 15, background: 'rgba(255,255,255,0.75)', fontSize: 25 }}>{section.emoji || '\u{1F48C}'}</span>
          <span className="min-w-0">
            <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 30, margin: 0, lineHeight: 1.1, ...gradTitle(P) }}>{section.name}</h1>
            <span className="block" style={{ fontSize: 12.5, fontWeight: 600, color: P.headSoft, marginTop: 3 }}>{list.length} {list.length === 1 ? 'post' : 'posts'}</span>
          </span>
        </div>
        {section.description && <p style={{ margin: '12px 2px 0', fontSize: 14, color: P.headSoft, lineHeight: 1.45 }}>{section.description}</p>}
      </div>
    );
    content = (
      <div data-testid="money-club-section-page">
        {section.id === 'wins' && <WinBanner P={P} text={winText} onShare={() => setShareOpen('win')} />}
        {section.id === 'guides' && <AskBanner P={P} onAsk={() => setShareOpen('question')} />}
        {list.length === 0 ? (
          <div className="text-center" style={{ borderRadius: 18, padding: '26px 18px', marginTop: 24, background: P.soft }}>
            <span className="block" style={{ fontSize: 28 }}>{section.emoji || '\u{1F48C}'}</span>
            <span className="block" style={{ fontSize: 15, fontWeight: 700, color: INK, marginTop: 6 }}>{section.name} {isPluralName(section.name) ? 'are' : 'is'} coming soon!</span>
          </div>
        ) : (
          <>
            <Label>Newest</Label>
            <FeatureCard post={list[0]} section={section} P={P} isNew={isNewPost(list[0])} onOpen={openPost} />
            {monthGroups(list.slice(1)).map((g) => (
              <div key={g.key}>
                <Label>{g.key}</Label>
                {listCard(g.posts.map((p) => <PostRow key={p.id} post={p} section={section} P={P} isNew={isNewPost(p)} onOpen={openPost} showSection={false} />))}
              </div>
            ))}
          </>
        )}
      </div>
    );
  } else {
    // Hub
    const pinned = byDate.filter((p) => p.pinned);
    const unpinned = byDate.filter((p) => !p.pinned);
    const latest = unpinned[0] || null;
    const more = unpinned.slice(1, 9);
    const counts = {};
    posts.forEach((p) => { if (p.section_id) counts[p.section_id] = (counts[p.section_id] || 0) + 1; });
    header = (
      <div data-testid="money-club-hub-head">
        <Masthead P={P} />
        {sections.length > 0 && <SectionMenu sections={sections} P={P} newIds={newSectionIds} counts={counts} onOpen={openSection} />}
      </div>
    );
    content = (
      <div data-testid="money-club-hub">

        {pinned.length > 0 && (
          <>
            <Label>Pinned</Label>
            {listCard(pinned.map((p) => <PostRow key={p.id} post={p} section={sectionFor(feed, p)} P={P} isNew={false} onOpen={openPost} />))}
          </>
        )}

        <Label>Latest</Label>
        {latest ? (
          <FeatureCard post={latest} section={sectionFor(feed, latest)} P={P} isNew={isNewPost(latest)} onOpen={openPost} />
        ) : (
          <div data-testid="money-club-welcome" style={{ border: tinted ? 0 : '1.5px solid #EEE9EB', boxShadow: tinted ? `0 6px 22px ${P.glow}` : 'none', borderRadius: 22, padding: 18, background: '#fff' }}>
            <span className="flex items-center" style={{ gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED }}>
              From the Worthly team
              <span className="rounded-full" style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 11, fontWeight: 700, padding: '3px 9px', letterSpacing: 0, textTransform: 'none' }}>Coming soon</span>
            </span>
            <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 27, margin: '8px 0 10px', color: '#1A1A1F', lineHeight: 1.15 }}>Welcome to the Money Club</h2>
            <p style={{ fontSize: 16, lineHeight: 1.55, margin: '0 0 10px', color: '#4B5563' }}>You're one of the first ones here! 💜</p>
            <p style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 10px', color: INK }}>This is your new money hub. Every Sunday we'll share a little money advice to help you start the week well, plus guides for your money goals, wins from the Worthly community and updates on what's new in the app.</p>
            <p style={{ fontSize: 13.5, lineHeight: 1.5, margin: 0, color: MUTED }}>The first posts are coming soon. We'll send you a notification when they're here.</p>
          </div>
        )}

        {more.length > 0 && (
          <>
            <Label>More from the club</Label>
            <MoreRow posts={more} feed={feed} P={P} isNewPost={isNewPost} onOpen={openPost} />
          </>
        )}

        <div style={{ height: 8 }} />
        <ShareCard P={P} early={!posts.length} onShare={() => setShareOpen('win')} onAsk={() => setShareOpen('question')} />

        <button type="button" onClick={openFacebook} className="w-full text-center" style={{ border: 0, background: 'none', padding: '8px 8px 4px', marginTop: 26, fontSize: 13, color: MUTED, lineHeight: 1.45, fontFamily: SANS }} data-testid="money-club-facebook">
          Want to chat with other users?{' '}
          <span style={{ color: INK, fontWeight: 700, whiteSpace: 'nowrap' }}>Join our Facebook group ›</span>
        </button>
      </div>
    );
  }

  // Soft theme tint in Pink, Blue and Warm; white in Neutral and when reading a post.
  return createPortal(
    <div ref={scrollRef} data-swipe-layer="1" className="fixed inset-0 overflow-y-auto wy-club-page" style={{ zIndex: 250, background: P.plan ? '#F4F1FB' : '#fff', fontFamily: SANS }} data-testid="money-club-page">
      {header && (
        <div className="relative overflow-hidden" style={{ background: P.headBg, color: INK }} data-testid="money-club-header">
          <div className="relative max-w-2xl mx-auto px-4" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)', paddingBottom: 22 }}>
            {backButton(backLabel)}
            {header}
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-4" style={{ paddingTop: header ? 0 : 'calc(env(safe-area-inset-top) + 10px)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 40px)' }}>
        {!header && backButton(backLabel)}
        {content}
      </div>
      {shareOpen && <ShareWinSheet key={shareOpen} kind={shareOpen} P={P} firstName={firstName} onClose={() => setShareOpen(null)} />}
    </div>,
    document.body
  );
};
